import { toRecoveryAction } from "@kovernow/flows/recovery";
import {
  buildHomeView,
  greetingName,
  homeQueryKeys,
  resolveProductTiles,
  type AttentionCardView,
} from "@kovernow/flows/home";
import { AttentionCard } from "@kovernow/react-ui/components/attention-card";
import { Button } from "@kovernow/react-ui/components/button";
import { ProductTile } from "@kovernow/react-ui/components/product-tile";
import { VaultSummary } from "@kovernow/react-ui/components/vault-summary";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { categoryIcon } from "../components/category-icons";
import { Page } from "../components/Page";
import { Recovery } from "../components/Recovery";
import { useServices } from "../core/app-context";
import { useAccountUser } from "../core/queries";
import { useT } from "../core/use-t";

/**
 * W-2 Home: greeting, then "Needs attention" (≤ 3 cards, one action each) or
 * "All good", then VaultSummary, then the one `cta` "Add item", then the
 * tenant's product tiles. The attention list is computed on the server.
 */
export function Home() {
  const t = useT();
  const { homeApi, config } = useServices();
  const { account } = useAccountUser();
  const dashboard = useQuery({ queryKey: homeQueryKeys.dashboard(), queryFn: () => homeApi.dashboard() });
  // Flag-gated tiles hide while flags are unknown; core tiles never wait.
  const flags = useQuery({ queryKey: homeQueryKeys.flags(), queryFn: () => homeApi.flags(), retry: false });
  const tiles = resolveProductTiles(config.productTiles, flags.data);
  const name = greetingName(account);

  return (
    <Page
      titleKey="nav.home"
      headingKey={name ? "home.welcomeNamed" : "home.welcome"}
      headingValues={name ? { name } : undefined}
    >
      <div data-testid="home" className="flex flex-col gap-8">
        <DashboardSection
          isPending={dashboard.isPending}
          error={dashboard.error}
          data={dashboard.data}
          onRetry={() => void dashboard.refetch()}
        />

        <div>
          <Button asChild variant="cta" size="touch">
            <Link to="/vault/add">{t("nav.addItem")}</Link>
          </Button>
        </div>

        {tiles.length > 0 ? (
          <section aria-labelledby="home-tiles" data-testid="product-tiles">
            <h2 id="home-tiles" className="text-lg font-semibold">
              {t("home.tilesTitle")}
            </h2>
            <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {tiles.map((tile) => (
                <li key={tile.category} data-category={tile.category}>
                  <ProductTile label={t(tile.labelKey)} icon={categoryIcon(tile.category)}>
                    <Link to={tile.to} className="h-full w-full" />
                  </ProductTile>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </Page>
  );
}

function DashboardSection({
  isPending,
  error,
  data,
  onRetry,
}: {
  isPending: boolean;
  error: unknown;
  data: Parameters<typeof buildHomeView>[0] | undefined;
  onRetry: () => void;
}) {
  const t = useT();
  const { format } = useServices();

  if (isPending) {
    return (
      <p aria-busy="true" className="text-muted-foreground">
        {t("common.loading")}
      </p>
    );
  }
  if (error || !data) {
    return (
      <div data-testid="dashboard-error">
        <Recovery action={toRecoveryAction(error)} onRetry={onRetry} retryable />
      </div>
    );
  }

  const view = buildHomeView(data);
  const { state, vault } = view;

  return (
    <>
      {state.kind === "firstTime" ? (
        <section aria-labelledby="home-status" data-testid="home-first-time">
          <h2 id="home-status" className="text-lg font-semibold">
            {t("home.firstTimeTitle")}
          </h2>
          <p className="mt-1 text-muted-foreground">{t("home.firstTimeBody")}</p>
        </section>
      ) : null}

      {state.kind === "allGood" ? (
        <section aria-labelledby="home-status" data-testid="home-all-good">
          <h2 id="home-status" className="text-lg font-semibold">
            {t("home.allGood", { count: state.protectedCount })}
          </h2>
        </section>
      ) : null}

      {state.kind === "attention" ? (
        <section aria-labelledby="home-status" data-testid="home-attention">
          <h2 id="home-status" className="text-lg font-semibold">
            {t("home.needsAttention", { count: state.count })}
          </h2>
          <Cards cards={state.cards} />
        </section>
      ) : null}

      {state.kind === "expired" ? (
        <section aria-labelledby="home-status" data-testid="home-expired">
          <h2 id="home-status" className="text-lg font-semibold">
            {t("home.expiredTitle")}
          </h2>
          <ul className="mt-3 flex flex-col divide-y rounded-lg border">
            {state.lapsed.map((l) => (
              <li key={l.itemId}>
                <Link to={l.to} className="flex min-h-12 items-center px-4 font-semibold underline-offset-4 hover:underline">
                  {l.itemName ? t("home.expiredRenew", { itemName: l.itemName }) : t("home.expiredRenewUnnamed")}
                </Link>
              </li>
            ))}
          </ul>
          {state.cards.length > 0 ? <Cards cards={state.cards} /> : null}
        </section>
      ) : null}

      {view.showVaultSummary ? (
        <VaultSummary
          data-testid="vault-summary"
          title={t("home.vault.title")}
          stats={[
            { id: "insured", label: t("home.vault.insured"), value: format.number(vault.insuredCount) },
            { id: "uninsured", label: t("home.vault.uninsured"), value: format.number(vault.uninsuredCount) },
            { id: "value", label: t("home.vault.insuredValue"), value: format.currency(vault.insuredValue) },
          ]}
          action={
            <Link to="/vault" className="inline-flex min-h-11 items-center text-sm font-semibold text-primary underline underline-offset-4">
              {t("home.vault.open")}
            </Link>
          }
        />
      ) : null}
    </>
  );
}

function Cards({ cards }: { cards: AttentionCardView[] }) {
  const t = useT();
  const { format } = useServices();
  return (
    <ul className="mt-3 flex flex-col gap-3">
      {cards.map((card) => {
        const values = { ...card.values, date: card.dueAt ? format.date(card.dueAt) : undefined };
        return (
          <li key={card.key}>
            <AttentionCard
              data-kind={card.kind}
              tone={card.tone}
              title={t(card.titleKey, values)}
              body={t(card.bodyKey, values)}
              action={
                <Button asChild variant="outline" size="touch">
                  <Link to={card.to}>{t(card.actionKey)}</Link>
                </Button>
              }
            />
          </li>
        );
      })}
    </ul>
  );
}
